import { useMutation, useQuery } from '@tanstack/react-query'
import { Alert, Button, Collapse, DatePicker, Descriptions, Modal, Select, Space, Spin, Table, Tag, Typography, message } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import React, { useEffect, useState } from 'react'
import { getCreditPackRefundQuote, recordPaymentOrderRefund, refundQueries } from '../../../../../Actions'
import {
  BillingHistoryItem,
  canCompleteRefund,
  CreditPackRefundQuote,
  creditPackReasonOf,
  deactivatesSubscription,
  creditPackBracketRows,
  formatRatio,
  PENALTY_BRACKET_LABEL,
  REFUND_FOLLOW_UP_ACTION_LABEL,
  REFUND_REASON_LABEL,
  RecordRefundRequest,
} from '../../../../../types/refund'
import { apiErrorMessage } from '../../../../../utils/apiError'
import { quoteOrReason } from '../../../../../utils/quoteError'
import useInvalidateRefunds from './useInvalidateRefunds'

type Props = {
  open: boolean
  workspaceId: number
  /** 크레딧 팩 결제 건 행. 구독 행은 `RefundQuoteModal` 소관이다 */
  item: BillingHistoryItem | null
  onClose: () => void
}

const money = (value?: number | null) => (value == null ? '-' : `${Number(value).toLocaleString()}원`)
const credit = (value?: number | null) => (value == null ? '-' : `${Number(value).toLocaleString()}크레딧`)

/**
 * 검산 `unused = MAX(0, MIN(granted, creditPackRemaining − grantedAfter))` (aivatar #537).
 *
 * **판정이 아니라 대조다.** 금액은 서버 산정 그대로 접수하고, 화면은 재료가 식에 맞는지만 보여준다.
 * 어긋나면 산정 재료와 결과가 서로를 부정하는 것이라 담당자가 원장을 봐야 한다.
 */
const recomputeUnused = (quote: CreditPackRefundQuote): number | null => {
  if (quote.granted == null || quote.creditPackRemaining == null || quote.grantedAfter == null) return null
  return Math.max(0, Math.min(Number(quote.granted), Number(quote.creditPackRemaining) - Number(quote.grantedAfter)))
}

/**
 * 원장 정밀도(소수 4자리)에서 같은 크레딧인가.
 *
 * 크레딧은 소수가 생긴다(프로모션 할인 차감 등). 부동소수 뺄셈은 1234.1 − 1000 = 234.09999999999991 처럼
 * 끝자리가 어긋나, 그대로 `!==` 로 비교하면 산정이 맞는데도 "검산 불일치" 경고가 뜬다.
 */
const sameCredit = (a: number, b: number): boolean => Math.round(a * 10000) === Math.round(b * 10000)

/**
 * 크레딧 팩 산정 미리보기 + 환불 접수 (환불대장 Phase 2 P2 · #280)
 *
 * 흐름은 구독 환불(`RefundQuoteModal`)과 같다 — **미리보기는 아무것도 기록하지 않고**, `환불 접수`
 * 를 누른 순간 산정값이 고정된다. 달라지는 것은 셋이다.
 *
 * - 대상이 청구서가 아니라 **결제 건**이다(D-121). 산정·현황·접수가 `payment-orders` API 다
 * - 사유를 고르지 않는다. 구간이 정한다(`creditPackReasonOf`)
 * - 확정하면 사유와 무관하게 **그 결제 건의 남은 크레딧이 전부 소멸**한다(D-124)
 */
export default function CreditPackRefundModal({ open, workspaceId, item, onClose }: Props) {
  const [referenceDate, setReferenceDate] = useState<Dayjs>(dayjs())
  /** 정정 대상. 같은 결제 건의 **철회된** 행만 지목할 수 있다(서버가 막는다, aivatar #502) */
  const [supersedesRefundId, setSupersedesRefundId] = useState<number | undefined>()
  const invalidate = useInvalidateRefunds(workspaceId)

  const paymentOrderId = item?.type === 'CREDIT_PACK' ? item.paymentOrderId : null

  // 모달을 다시 열 때 지난 기준일·정정 대상이 남아 있으면 다른 결제 건에 그 값이 적용된다
  useEffect(() => {
    if (open) {
      setReferenceDate(dayjs())
      setSupersedesRefundId(undefined)
    }
  }, [open, paymentOrderId])

  const isoDate = referenceDate.format('YYYY-MM-DD')

  const { data: result, isFetching } = useQuery({
    // 기준일이 키에 들어간다 — 날짜를 바꾸면 다시 산정한다.
    // gcTime 0: 모달을 닫았다 열면 낡은 산정이 보이지 않아야 한다.
    queryKey: ['refund-quote', 'credit-pack', paymentOrderId, isoDate],
    queryFn: () => quoteOrReason<CreditPackRefundQuote>(() => getCreditPackRefundQuote(paymentOrderId!, isoDate)),
    enabled: open && paymentOrderId != null,
    gcTime: 0,
    retry: false,
  })

  const quote = result?.ok ? result.data : null

  const { data: view } = useQuery({
    ...refundQueries.paymentOrderView(paymentOrderId ?? 0),
    enabled: open && paymentOrderId != null,
  })

  const withdrawnRefunds = (view?.refunds ?? []).filter((refund) => refund.status === 'WITHDRAWN')

  /**
   * 아직 확정되지 않은 구독취소 (전달본 P2 · SC-OPT4).
   *
   * 종료 선택권(구간 2)은 구독취소가 **처리 완료된 뒤**에야 나온다 — 확정이 구독 종료 행과 종료일을
   * 만들기 때문이다(`CreditPackRefundService.terminatedOn`). 그 전에 산정하면 구간 3 으로 잡혀 위약금이
   * 붙는다. 서버가 이 상태를 따로 알려주지 않으므로 대장에서 같은 워크스페이스의 접수·보류 중인
   * 구독 종료 유형 행을 찾는다. **막지 않는다** — 구독취소와 무관한 단건 환불도 있다.
   */
  const { data: ledger } = useQuery({ ...refundQueries.ledger(workspaceId), enabled: open })
  const pendingCancellations = (ledger ?? []).filter(
    (row) => row.paymentOrderId == null && canCompleteRefund(row.status) && deactivatesSubscription(row.reason),
  )

  const reason = quote ? creditPackReasonOf(quote.penaltyBracket) : null

  const { mutateAsync: receive, isPending } = useMutation({
    mutationFn: (body: RecordRefundRequest) => recordPaymentOrderRefund(paymentOrderId!, body),
  })

  const submit = async () => {
    if (!quote || !reason || paymentOrderId == null || !quote.amount) return

    try {
      await receive({
        status: 'QUOTED',
        // 🔴 산정값을 가공하지 않는다. 서버는 접수 시 산정을 다시 하지 않으므로
        // 여기서 반올림하거나 다시 계산하면 근거와 금액이 갈린다(aivatar #488 (b))
        amount: quote.amount,
        penalty: quote.penalty,
        reason,
        calculation: quote.calculation,
        // 근거 3값. 비율 그대로 넘긴다(0.05 = 5%) — 서버가 퍼센트로 바꿔 저장한다.
        // `?? undefined` 여야 한다: 요율 0 은 버릴 값이 아니라 보낼 값이다
        penaltyBracket: quote.penaltyBracket ?? undefined,
        penaltyRate: quote.penaltyRate ?? undefined,
        // 접수 스냅샷. 확정 때 서버가 "접수 뒤 사용이 바뀌었나" 를 이 값으로 판정한다
        usageRatio: quote.usageRatio ?? undefined,
        supersedesRefundId,
      })
    } catch (e) {
      message.error(
        apiErrorMessage(e) ??
          '접수하지 못했습니다. 결제 성공 건인지, 환불 합계가 결제액을 넘지 않는지 확인하세요. (서버가 사유를 주지 않았습니다)',
      )
      return
    }

    message.success('환불을 접수했습니다. 결제대행사(단건 상점)에서 취소를 실행한 뒤 처리 완료를 확정하세요.')
    await invalidate()
    onClose()
  }

  /** 기준일이 오늘이 아니면 한 번 더 묻는다 — 산정이 이 날짜로 영구히 고정된다 */
  const confirmAndSubmit = () => {
    if (referenceDate.isSame(dayjs(), 'day')) {
      submit()
      return
    }

    Modal.confirm({
      title: '이대로 접수할까요?',
      content: (
        <span>
          산정 기준일이 오늘이 아닌 <b>{isoDate}</b> 입니다. 접수하면 이 날짜의 산정으로 고정되며 수정할 수 없습니다.
        </span>
      ),
      okText: '접수',
      cancelText: '취소',
      onOk: submit,
    })
  }

  const recomputed = quote ? recomputeUnused(quote) : null
  const unusedMismatch = !!quote && recomputed != null && quote.unused != null && !sameCredit(recomputed, Number(quote.unused))

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={720}
      title="크레딧 팩 환불 산정 미리보기"
      footer={[
        <Button key="close" onClick={onClose}>
          닫기
        </Button>,
        // 산정 0원이면 접수할 것이 없다. 서버도 0원 접수는 400 이다
        <Button key="submit" type="primary" loading={isPending} disabled={!quote || !quote.amount} onClick={confirmAndSubmit}>
          환불 접수
        </Button>,
      ]}
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="이 화면은 아무것도 기록하지 않습니다"
        description="행이 생기는 것은 '환불 접수' 를 누른 순간이며, 그때 산정값이 고정되어 이후 수정할 수 없습니다."
      />

      {pendingCancellations.length ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="처리 완료되지 않은 구독 종료 환불이 있습니다"
          description={
            <Space direction="vertical" size={2}>
              <span>
                구독취소와 함께 신청한 단건 환불이면, 구독취소를 먼저 처리 완료로 확정한 뒤 산정하세요. 확정 전에는 구독
                종료일이 없어 종료 선택권(구간 2) 대신 위약금이 붙는 구간으로 산정됩니다.
              </span>
              <span>
                {pendingCancellations.map((row) => (
                  <Tag key={row.refundId}>
                    #{row.refundId} · {REFUND_REASON_LABEL[row.reason] ?? row.reason}
                  </Tag>
                ))}
              </span>
            </Space>
          }
        />
      ) : null}

      <Space style={{ marginBottom: 12 }}>
        <span>산정 기준일 (환불 신청일)</span>
        <DatePicker allowClear={false} value={referenceDate} onChange={(value) => value && setReferenceDate(value)} />
      </Space>

      <Spin spinning={isFetching}>
        {result && !result.ok ? (
          // 400 은 오류가 아니라 "산정할 수 없다 + 이유" 다. 원문을 그대로 띄운다 —
          // 소멸한 건은 재량 처리(수동 기록), 잔량을 믿을 수 없으면 수기 산정이라고 문장이 말한다
          <Alert type="warning" showIcon message="산정할 수 없습니다" description={result.reason} />
        ) : null}

        {quote ? (
          <>
            {quote.warning ? (
              // 폴백 잔량 산정. 금액은 나왔지만 미사용분이 실제보다 적게 잡혔을 수 있다
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 12 }}
                message="확인이 필요한 산정입니다"
                description={quote.warning}
              />
            ) : null}

            <Descriptions title="대상 결제 건" size="small" column={2} bordered style={{ marginBottom: 12 }}>
              <Descriptions.Item label="항목">
                {item?.itemName ?? '-'} <Tag color="geekblue">단건 상점</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="결제 건">#{quote.orderId}</Descriptions.Item>
              <Descriptions.Item label="구매일">{quote.purchasedOn ?? '-'}</Descriptions.Item>
              <Descriptions.Item label="소멸일">{quote.expiresOn ?? '-'}</Descriptions.Item>
              <Descriptions.Item label="실결제액">{money(quote.charge)}</Descriptions.Item>
              <Descriptions.Item label="지급 크레딧">{credit(quote.granted)}</Descriptions.Item>
              <Descriptions.Item label="결제 고유번호">
                <Typography.Text copyable={!!item?.paymentNumber}>{item?.paymentNumber ?? '-'}</Typography.Text>
              </Descriptions.Item>
              <Descriptions.Item label="주문번호">
                <Typography.Text copyable={!!quote.merchantUid}>{quote.merchantUid ?? '-'}</Typography.Text>
              </Descriptions.Item>
            </Descriptions>

            {/* 금액보다 어느 구간이 왜 적용됐는지가 먼저 보여야 한다 — 고객 문의에 그 이유를 답한다(환불대장 v4 P2) */}
            <Typography.Title level={5}>구간 판정</Typography.Title>
            <Table
              size="small"
              bordered
              pagination={false}
              style={{ marginBottom: 12 }}
              rowKey="key"
              dataSource={creditPackBracketRows(quote)}
              columns={[
                {
                  title: '구간',
                  key: 'name',
                  render: (t, row) => (row.applied ? <Typography.Text strong>{row.name}</Typography.Text> : row.name),
                },
                { title: '조건', dataIndex: 'condition', key: 'condition' },
                {
                  title: '이 건',
                  key: 'verdict',
                  render: (t, row) =>
                    row.applied ? <Tag color="blue">{row.verdict}</Tag> : <Typography.Text type="secondary">{row.verdict}</Typography.Text>,
                },
              ]}
            />

            <Descriptions title="산정 결과" size="small" column={2} bordered style={{ marginBottom: 12 }}>
              <Descriptions.Item label="산정 기준일">{quote.referenceDate}</Descriptions.Item>
              <Descriptions.Item label="구독 상태">
                {quote.subscribed ? (
                  // 구독 중이거나 종료 뒤 재구독했다 — 종료 선택권이 없다
                  <Tag>구독 중 · 종료 선택권 없음</Tag>
                ) : (
                  <Tag color="blue">구독 종료 {quote.terminatedOn ?? '-'}</Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="위약금 구간" span={2}>
                {quote.penaltyBracket ? PENALTY_BRACKET_LABEL[quote.penaltyBracket] : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="사용률">{formatRatio(quote.usageRatio)}</Descriptions.Item>
              <Descriptions.Item label="미사용">
                {credit(quote.unused)} ({formatRatio(quote.unusedRatio)})
              </Descriptions.Item>
              <Descriptions.Item label="수수료 전">{money(quote.beforeFee)}</Descriptions.Item>
              <Descriptions.Item label="위약금">
                {money(quote.penalty)} ({formatRatio(quote.penaltyRate)})
              </Descriptions.Item>
              <Descriptions.Item label="최종 환불액 (부가세 포함)" span={2}>
                <Typography.Text strong>{money(quote.amount)}</Typography.Text>
              </Descriptions.Item>
            </Descriptions>

            {quote.amount === 0 ? (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 12 }}
                message="이 결제 건에 남은 크레딧이 없습니다"
              />
            ) : null}

            <Collapse
              size="small"
              style={{ marginBottom: 12 }}
              items={[
                {
                  key: 'materials',
                  label: '산정 재료',
                  children: (
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Descriptions size="small" column={2} bordered>
                        <Descriptions.Item label="이 결제 건 지급">{credit(quote.granted)}</Descriptions.Item>
                        <Descriptions.Item label="크레딧 팩 남은 합계">{credit(quote.creditPackRemaining)}</Descriptions.Item>
                        <Descriptions.Item label="이후 결제된 크레딧 팩">{credit(quote.grantedAfter)}</Descriptions.Item>
                        <Descriptions.Item label="잔량 기준">
                          {quote.poolBasis === 'FALLBACK' ? <Tag color="orange">폴백</Tag> : (quote.poolBasis ?? '-')}
                        </Descriptions.Item>
                        <Descriptions.Item label="검산" span={2}>
                          MAX(0, MIN({credit(quote.granted)}, {credit(quote.creditPackRemaining)} −{' '}
                          {credit(quote.grantedAfter)})) = {credit(recomputed)}
                        </Descriptions.Item>
                        <Descriptions.Item label="산정 내역" span={2}>
                          {/* 금액이 어떻게 나왔는지에 대한 서버의 설명이다. 화면 계산으로 대체하지 않는다 */}
                          {quote.calculation ?? '-'}
                        </Descriptions.Item>
                      </Descriptions>
                      {unusedMismatch ? (
                        <Alert
                          type="error"
                          showIcon
                          message="검산이 산정 미사용분과 다릅니다"
                          description={`재료로 다시 셈한 값은 ${credit(recomputed)}, 산정은 ${credit(quote.unused)} 입니다. 접수 전에 크레딧 원장을 확인하세요.`}
                        />
                      ) : null}
                    </Space>
                  ),
                },
              ]}
            />

            <Space direction="vertical" style={{ width: '100%' }}>
              {/* 사유는 구간이 정한다 — 고르게 하면 구간과 사유가 갈린 행이 생긴다 */}
              <span>
                환불 사유: <b>{reason ? REFUND_REASON_LABEL[reason] : '-'}</b>{' '}
                <Typography.Text type="secondary">(산정 구간으로 정해집니다)</Typography.Text>
              </span>
              <Typography.Text type="secondary">
                처리 완료를 확정하면 <b>{REFUND_FOLLOW_UP_ACTION_LABEL.REVOKE_CREDIT_PACK}</b> 이(가) 실행됩니다 — 부분
                환불이어도 남은 크레딧은 전부 없어집니다.
              </Typography.Text>

              <span>정정 대상 (선택)</span>
              <Select
                allowClear
                style={{ width: '100%' }}
                placeholder={
                  withdrawnRefunds.length ? '이 접수가 정정하는 원 환불 기록' : '이 결제 건에 철회된 기록이 없습니다'
                }
                disabled={!withdrawnRefunds.length}
                value={supersedesRefundId}
                options={withdrawnRefunds.map((refund) => ({
                  value: refund.id,
                  label: `#${refund.id} · ${money(refund.amount)} · ${REFUND_REASON_LABEL[refund.reason] ?? refund.reason}`,
                }))}
                onChange={setSupersedesRefundId}
              />
            </Space>
          </>
        ) : null}
      </Spin>
    </Modal>
  )
}
